import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { TypedMemo } from '../../common/types';
import { ErrorView } from '../../common/views/ErrorView/ErrorView';
import { LoadingView } from '../../common/views/LoadingView';
import { useEmbedHeight } from '../../coverEditor/publicMode/useEmbedHeight';
import { useNoIndex } from '../../coverEditor/publicMode/useNoIndex';
import { useGetMusicVideoShowcaseQuery } from '../musicVideoEditorApi';
import { isMusicVideoDemoEmbed } from './musicVideoDemoPath';
import { trackShowcaseOpened } from './showcaseAnalytics';
import { musicVideoDemoHeightMessage } from './showcaseEmbedHeight';
import { ShowcaseEditor } from './ShowcaseEditor';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the editor; each part uses its own slice of it */
import styles from '../MusicVideoEditor.module.scss';
/* eslint-enable css-modules/no-unused-class */

/**
 * The Studio showcase: `/app/music-video-demo` (spec 2026-10-10 Studio showcase §4, design c). No
 * login, for every visitor (a signed-in one too), not indexed. `?embed=1` is the demo inside the
 * frame of music24.com/music-video-maker: it posts its height to the page around it. It reads the
 * showcase answer (`GET /public/music-video-showcase`) and opens the one editor in showcase mode.
 */
const MusicVideoShowcaseViewComponent: React.FC = () => {
  const { search } = useLocation();
  const isEmbed = isMusicVideoDemoEmbed(search);
  const showcase = useGetMusicVideoShowcaseQuery();
  const { data } = showcase;
  useNoIndex();
  const songMap = data?.editor.songMap ?? null;
  // The website reads the first height message as "the editor is on screen". The loading and the
  // error states send none, so the website shows its own failure state and retry.
  useEmbedHeight(isEmbed && songMap !== null, musicVideoDemoHeightMessage);

  const isOpenSent = useRef(false);
  useEffect(() => {
    if (data === undefined || isOpenSent.current) {
      return;
    }
    isOpenSent.current = true;
    trackShowcaseOpened(isEmbed);
  }, [data, isEmbed]);

  let body: React.ReactNode;
  if (data === undefined && showcase.error !== undefined) {
    body = (
      <div className={styles.fullState}>
        <ErrorView error={showcase.error} retry={() => showcase.refetch().catch(console.error)} />
      </div>
    );
  } else if (data === undefined || songMap === null) {
    body = (
      <div className={styles.fullState} role="status">
        <LoadingView />
      </div>
    );
  } else {
    body = <ShowcaseEditor showcase={data} songMap={songMap} isEmbed={isEmbed} />;
  }
  // The showcase covers the window like the account editor, so it gives back the scrollbar room of
  // the page (appStyle.scss). Else the embed on music24.com shows a dark strip at its right edge.
  return (
    <div className={styles.screen} data-fullscreen-editor>
      {body}
    </div>
  );
};

export const MusicVideoShowcaseView = TypedMemo(MusicVideoShowcaseViewComponent);
